/**
 * Afroyla chrome header island — mega-nav hover-intent, scroll->solid state, mobile
 * nav drawer, cart-count refresh, search/cart event contract. Plain vanilla JS, no
 * framework, light DOM. See plans/20260820-horizon-rebuild/phase-02-chrome-header-nav.md
 * "State machine" for the exact trigger/timing table this implements.
 */
(function () {
  'use strict';

  // Theme-editor section re-renders re-execute this script; without a guard the
  // document-level listeners stack on every re-render.
  if (window.__aflHeaderInit) return;
  window.__aflHeaderInit = true;

  var header = document.getElementById('afl-header');
  if (!header) return;

  // Horizon's --header-height contract: stock consumers (_cart-summary, product
  // information, hero) read these; the stock header-component that set them is gone.
  var setHeaderHeightVars = function () {
    var h = header.offsetHeight || 64;
    document.documentElement.style.setProperty('--header-height', h + 'px');
    document.documentElement.style.setProperty('--header-group-height', h + 'px');
    document.documentElement.style.setProperty('--afroyla-header-h', h + 'px');
  };
  setHeaderHeightVars();
  window.addEventListener('resize', setHeaderHeightVars, { passive: true });

  var HOVER_OPEN_DELAY = 140;
  var HOVER_CLOSE_DELAY = 120;

  var openTimer = null;
  var closeTimer = null;
  var currentPanel = null;

  var triggers = {
    style: document.getElementById('afl-trigger-style'),
    bags: document.getElementById('afl-trigger-bags'),
  };
  var panels = {
    style: document.getElementById('afl-panel-style'),
    bags: document.getElementById('afl-panel-bags'),
  };

  function clearTimers() {
    if (openTimer) {
      clearTimeout(openTimer);
      openTimer = null;
    }
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
  }

  function closePanelImmediate() {
    if (!currentPanel) return;
    panels[currentPanel].hidden = true;
    triggers[currentPanel].setAttribute('aria-expanded', 'false');
    currentPanel = null;
  }

  function openPanel(name) {
    if (currentPanel === name) return;
    closePanelImmediate();
    currentPanel = name;
    panels[name].hidden = false;
    triggers[name].setAttribute('aria-expanded', 'true');
  }

  function scheduleOpen(name) {
    clearTimers();
    openTimer = setTimeout(function () {
      openPanel(name);
    }, HOVER_OPEN_DELAY);
  }

  function scheduleClose() {
    clearTimers();
    closeTimer = setTimeout(function () {
      closePanelImmediate();
    }, HOVER_CLOSE_DELAY);
  }

  Object.keys(triggers).forEach(function (name) {
    var trigger = triggers[name];
    if (!trigger) return;

    trigger.addEventListener('mouseenter', function () {
      scheduleOpen(name);
    });

    // Touch/click devices don't hover — toggle immediately on click.
    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      clearTimers();
      if (currentPanel === name) {
        closePanelImmediate();
      } else {
        openPanel(name);
      }
    });

    trigger.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        clearTimers();
        if (currentPanel === name) {
          closePanelImmediate();
        } else {
          openPanel(name);
        }
      }
    });
  });

  header.addEventListener('mouseenter', clearTimers);
  header.addEventListener('mouseleave', scheduleClose);

  header.querySelectorAll('[data-afl-plain-link]').forEach(function (link) {
    link.addEventListener('mouseenter', scheduleClose);
  });

  header.addEventListener('focusout', function () {
    requestAnimationFrame(function () {
      if (!header.contains(document.activeElement)) closePanelImmediate();
    });
  });

  document.addEventListener('click', function (event) {
    if (currentPanel && !header.contains(event.target)) closePanelImmediate();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && currentPanel) closePanelImmediate();
  });

  // --- Scroll -> solid state (homepage T1 variant only) -------------------------
  if (header.hasAttribute('data-transparent-home')) {
    var setSolid = function (solid) {
      header.setAttribute('data-solid', solid ? 'true' : 'false');
    };

    // The hero pulls itself under the bar, so its top sentinel rests at
    // y≈32 (below the announcement strip) — INSIDE the old observer's
    // "-headerHeight" dead margin, which meant isIntersecting was false on
    // load and the bar stayed solid forever. And ≥990px Horizon scrolls
    // .page-wrapper, not the window, so the window.scrollY fallback never
    // fired either. Read whichever scroller actually scrolls instead.
    var pageWrapper = document.querySelector('.page-wrapper');
    var readScroll = function () {
      if (pageWrapper && pageWrapper.scrollHeight > pageWrapper.clientHeight + 10) {
        return pageWrapper.scrollTop;
      }
      return window.scrollY || document.documentElement.scrollTop || 0;
    };
    var onScroll = function () {
      setSolid(readScroll() > 8);
    };
    onScroll();
    if (pageWrapper) pageWrapper.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // --- Cart count live refresh ---------------------------------------------------
  var cartCountEl = document.getElementById('afl-cart-count');
  document.addEventListener('shopify:cart:lines-update', function (event) {
    if (!cartCountEl) return;
    // Some Horizon dispatch sites resolve without a cart object — fall back to
    // detail.itemCount like Horizon's own cart-icon consumer does.
    var applyCount = function (count) {
      if (typeof count === 'number') cartCountEl.textContent = String(count);
    };
    var detail = event.detail || {};
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise
        .then(function (result) {
          var cart = result && result.cart;
          if (cart && typeof cart.totalQuantity === 'number') applyCount(cart.totalQuantity);
          else if (typeof detail.itemCount === 'number') applyCount(detail.itemCount);
        })
        .catch(function () {});
    } else if (typeof detail.itemCount === 'number') {
      applyCount(detail.itemCount);
    }
  });

  // --- Search / cart event contract (phase-10 / phase-07 consumers) --------------
  // aria-expanded on this control is owned by assets/afroyla-search-band.js — it is
  // the only script that also knows about the band's close (Esc) paths, so
  // it is the single source of truth (avoids a desync where this handler flips the
  // attribute independently of the band's real open/closed state). This click
  // handler only dispatches the toggle intent.
  var searchToggle = document.getElementById('afl-search-toggle');
  if (searchToggle) {
    searchToggle.addEventListener('click', function () {
      document.dispatchEvent(new CustomEvent('afroyla:search:toggle'));
    });
  }

  ['afl-cart-toggle', 'afl-cart-toggle-mobile'].forEach(function (id) {
    var el = document.getElementById(id);
    if (el) {
      el.addEventListener('click', function () {
        document.dispatchEvent(new CustomEvent('afroyla:cart:toggle'));
      });
    }
  });

  // --- Mobile nav drawer -----------------------------------------------------------
  var drawer = document.getElementById('afl-nav-drawer');
  var drawerOpenBtn = document.getElementById('afl-drawer-open');
  var savedScrollY = 0;

  function lockScroll(lock) {
    if (lock) {
      savedScrollY = window.scrollY;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      window.scrollTo(0, savedScrollY);
    }
  }

  function showLevel(name) {
    if (!drawer) return;
    drawer.querySelectorAll('[data-afl-level]').forEach(function (level) {
      level.hidden = level.getAttribute('data-afl-level') !== name;
    });
  }

  function closeDrawer() {
    if (!drawer) return;
    if (typeof drawer.close === 'function') {
      drawer.close();
    } else {
      drawer.removeAttribute('open');
      lockScroll(false);
    }
  }

  if (drawer && drawerOpenBtn) {
    drawerOpenBtn.addEventListener('click', function () {
      showLevel('root');
      if (typeof drawer.showModal === 'function') {
        drawer.showModal();
      } else {
        drawer.setAttribute('open', '');
      }
      lockScroll(true);
    });

    // Native <dialog> close event fires on Escape too — single place to restore scroll.
    drawer.addEventListener('close', function () {
      lockScroll(false);
    });

    drawer.querySelectorAll('[data-afl-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', closeDrawer);
    });

    drawer.querySelectorAll('[data-afl-go]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        showLevel(btn.getAttribute('data-afl-go'));
      });
    });

    drawer.addEventListener('click', function (event) {
      if (event.target === drawer) closeDrawer();
    });
  }
})();
